// src/components/CarouselSection.jsx
import React, { useEffect, useRef, useState } from "react";
import c1 from "../assets/c1.webp";
import c2 from "../assets/c2.jpeg";
import c3 from "../assets/c3.webp";
import c1j from "../assets/c1.jpeg";



export default function CarouselSection({
  interval = 4500,
  transitionMs = 800,
  showControls = true,
  showDots = true,
  // tweak these to control how the carousel relates to your navbar
  navbarHeight = 135,   // match your Navbar height
  navOverlap = 28,      // how many px the carousel "peeks" under the navbar
  extraGrow = 120,      // additional growth to make carousel taller
  minAllowed = 560,     // minimum pixel height for small windows
}) {
  // requested order: c2, c3, c1
  const images = [c1, c1j, c3, c2];
  const count = images.length;

  const [index, setIndex] = useState(0);
  const [isHovering, setIsHovering] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const timerRef = useRef(null);
  const touchStartX = useRef(0);
  const touchEndX = useRef(0);

  const [carouselHeight, setCarouselHeight] = useState(() => {
    if (typeof window === "undefined") return 900;
    const isMobileView = window.innerWidth <= 768;
    if (isMobileView) {
      // Mobile: use more viewport height for better visibility
      return Math.max(450, window.innerHeight * 0.6);
    }
    return Math.max(minAllowed, window.innerHeight - navbarHeight + extraGrow + navOverlap);
  });

  useEffect(() => {
    function handleResize() {
      const isMobileView = window.innerWidth <= 768;
      setIsMobile(isMobileView);
      
      if (isMobileView) {
        // Mobile: 60% of viewport height, min 450px
        const h = Math.max(450, window.innerHeight * 0.6);
        setCarouselHeight(h);
      } else {
        // Desktop: original calculation
        const h = Math.max(minAllowed, window.innerHeight - navbarHeight + extraGrow + navOverlap);
        setCarouselHeight(h);
      }
    }
    window.addEventListener("resize", handleResize);
    handleResize();
    return () => window.removeEventListener("resize", handleResize);
  }, [navbarHeight, extraGrow, navOverlap, minAllowed]);

  // autoplay
  useEffect(() => {
    if (isHovering) return;
    timerRef.current = setTimeout(() => setIndex(i => (i + 1) % count), interval);
    return () => clearTimeout(timerRef.current);
  }, [index, isHovering, interval, count]);

  // keyboard nav
  useEffect(() => {
    const handler = (e) => {
      if (e.key === "ArrowLeft") prev();
      if (e.key === "ArrowRight") next();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  const go = (i) => setIndex(((i % count) + count) % count);
  const next = () => go(index + 1);
  const prev = () => go(index - 1);

  // Touch/swipe handlers
  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchMove = (e) => {
    touchEndX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = () => {
    const swipeThreshold = 50; // minimum distance for a swipe
    const diff = touchStartX.current - touchEndX.current;

    if (Math.abs(diff) > swipeThreshold) {
      if (diff > 0) {
        // Swiped left -> next
        next();
      } else {
        // Swiped right -> prev
        prev();
      }
    }

    touchStartX.current = 0;
    touchEndX.current = 0;
  };

  const prefersReduced =
    typeof window !== "undefined" &&
    window.matchMedia &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  return (
    <section
      aria-label="Church Gallery"
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
      style={{
        width: "100vw",
        position: "relative",
        left: "50%",
        right: "50%",
        marginLeft: "-50vw",
        marginRight: "-50vw",
        overflow: "hidden",
        zIndex: 1,
        // Mobile: no overlap, desktop: overlap for visual effect
        marginTop: isMobile ? "0px" : `-${navOverlap}px`,
      }}
    >
      <style>{`
        /* Mobile-specific carousel adjustments */
        @media (max-width: 768px) {
          .carousel-container {
            min-height: 450px !important;
          }
          
          .carousel-image {
            object-fit: cover;
            object-position: center center;
          }
        }
        
        @media (max-width: 480px) {
          .carousel-container {
            min-height: 400px !important;
          }
        }
      `}</style>

      <div
        className="carousel-container relative overflow-hidden w-full"
        style={{
          height: `${carouselHeight}px`,
          minHeight: isMobile ? "450px" : `${minAllowed}px`,
          maxHeight: isMobile ? "70vh" : "100vh",
          backgroundColor: "#000",
        }}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        {images.map((src, i) => {
          const visible = i === index;
          // Special positioning for different images
          const isFirstPic = i === 0; // 1st image - show more bottom
          const isSecondPic = i === 1; // 2nd image - show more bottom, zoomed out
          const isThirdPic = i === 2; // 3rd image - zoomed out more
          const isFourthPic = i === 3; // 4th image - zoomed out more
          
          let bgPosition = "center center";
          let bgSize = "cover";
          
          // Desktop positioning
          if (!isMobile) {
            if (isFirstPic) {
              bgPosition = "center 95%"; // show way more bottom, cut off more top (hide fan blades)
              bgSize = "95%";
            }
            if (isSecondPic) {
              bgPosition = "center 80%";
              bgSize = "95%"; // zoom out
            }
            if (isThirdPic) {
              bgPosition = "center 30%";
              bgSize = "80%"; // max zoom out
            }
            if (isFourthPic) {
              bgPosition = "60% 20%"; // show more cross/right side, cut building/left side
              bgSize = "80%"; // max zoom out
            }
          } else {
            // Mobile positioning - more conservative, centered approach
            if (isFirstPic) bgPosition = "center 55%";
            if (isSecondPic) bgPosition = "center 60%";
            if (isThirdPic) bgPosition = "center 40%";
            if (isFourthPic) bgPosition = "center 35%";
            // Keep cover on mobile for better fit
            bgSize = "cover";
          }
          
          return (
            <div
              key={i}
              className="absolute inset-0"
              style={{
                opacity: visible ? 1 : 0,
                transition: `opacity ${transitionMs}ms ease-in-out`,
                zIndex: visible ? 20 : 10,
                pointerEvents: visible ? "auto" : "none",
              }}
            >
              <div
                className="carousel-image w-full h-full"
                style={{
                  backgroundImage: `url(${src})`,
                  backgroundSize: bgSize,
                  backgroundPosition: bgPosition,
                  // Less aggressive transform on mobile
                  transform: visible && !prefersReduced 
                    ? isMobile 
                      ? "scale(1.03)" 
                      : "scale(1.05) translateY(-6px)" 
                    : "scale(1) translateY(0)",
                  transition: `transform ${transitionMs * 1.05}ms cubic-bezier(.22,.9,.32,1)`,
                  filter: "brightness(0.95)",
                  width: "100%",
                  height: "100%",
                }}
              />
            </div>
          );
        })}

        {/* Controls - amber gradient arrows (hidden on mobile) */}
        {showControls && !isMobile && (
          <>
            <button
              onClick={prev}
              aria-label="Previous slide"
              className="absolute z-30 flex items-center justify-center transition-all duration-300"
              style={{
                left: isMobile ? "12px" : "24px",
                top: "50%",
                transform: "translateY(-50%)",
                width: isMobile ? "40px" : "48px",
                height: isMobile ? "40px" : "48px",
                background: "transparent",
                border: "none",
                cursor: "pointer",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = "translateY(-50%) scale(1.2)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "translateY(-50%) scale(1)";
              }}
            >
              <svg 
                width={isMobile ? "32" : "40"} 
                height={isMobile ? "32" : "40"} 
                viewBox="0 0 24 24" 
                fill="none" 
                aria-hidden
                style={{
                  filter: "drop-shadow(0 3px 10px rgba(0, 0, 0, 0.4))",
                }}
              >
                <defs>
                  <linearGradient id="arrowGradient1" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" style={{ stopColor: "#fbbf24", stopOpacity: 1 }} />
                    <stop offset="50%" style={{ stopColor: "#f59e0b", stopOpacity: 1 }} />
                    <stop offset="100%" style={{ stopColor: "#d97706", stopOpacity: 1 }} />
                  </linearGradient>
                </defs>
                <path d="M15 18l-6-6 6-6" stroke="url(#arrowGradient1)" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>

            <button
              onClick={next}
              aria-label="Next slide"
              className="absolute z-30 flex items-center justify-center transition-all duration-300"
              style={{
                right: isMobile ? "12px" : "24px",
                top: "50%",
                transform: "translateY(-50%)",
                width: isMobile ? "40px" : "48px",
                height: isMobile ? "40px" : "48px",
                background: "transparent",
                border: "none",
                cursor: "pointer",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = "translateY(-50%) scale(1.2)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "translateY(-50%) scale(1)";
              }}
            >
              <svg 
                width={isMobile ? "32" : "40"} 
                height={isMobile ? "32" : "40"} 
                viewBox="0 0 24 24" 
                fill="none" 
                aria-hidden
                style={{
                  filter: "drop-shadow(0 3px 10px rgba(0, 0, 0, 0.4))",
                }}
              >
                <defs>
                  <linearGradient id="arrowGradient2" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" style={{ stopColor: "#fbbf24", stopOpacity: 1 }} />
                    <stop offset="50%" style={{ stopColor: "#f59e0b", stopOpacity: 1 }} />
                    <stop offset="100%" style={{ stopColor: "#d97706", stopOpacity: 1 }} />
                  </linearGradient>
                </defs>
                <path d="M9 6l6 6-6 6" stroke="url(#arrowGradient2)" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
          </>
        )}

        {/* Dots - responsive sizing and positioning */}
        {showDots && (
          <div 
            className="absolute left-1/2 -translate-x-1/2 z-40 flex items-center"
            style={{
              bottom: isMobile ? "16px" : "32px",
              gap: isMobile ? "8px" : "12px",
            }}
          >
            {images.map((_, i) => (
              <button
                key={i}
                onClick={() => go(i)}
                aria-label={`Go to slide ${i + 1}`}
                className={`rounded-full transition-all duration-300`}
                style={{
                  width: isMobile ? "10px" : "14px",
                  height: isMobile ? "10px" : "14px",
                  backgroundColor: i === index ? "#d97706" : "rgba(255,255,255,0.8)",
                  border: i === index ? "none" : "1px solid rgba(255,255,255,0.7)",
                  transform: i === index ? "scale(1.15)" : "scale(1)",
                  boxShadow: i === index ? "0 4px 10px rgba(217,119,6,0.3)" : "none",
                }}
              />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}